import { supportedLanguages, type SupportedLanguage } from '@plato/app-meta/locale';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';

function isSupportedLanguage(language: unknown): language is SupportedLanguage {
    return (supportedLanguages as readonly unknown[]).includes(language);
}

/**
 * Headless consumer of the `?lng=` param that LanguageRedirect carries over
 * from the legacy /en and /de prefixes. The language itself is not touched —
 * it follows the user's stored preference / tenant default — the visit is
 * only reported, to measure whether such links are still in circulation.
 *
 * The param is consumed so reloads and copied URLs stay clean.
 */
export function LegacyLanguageUrl() {
    const [searchParams, setSearchParams] = useSearchParams();
    const sendEvent = useAnalytics();

    const urlLanguage = searchParams.get('lng');

    useEffect(() => {
        if (urlLanguage === null) {
            return;
        }

        setSearchParams(
            (previous) => {
                previous.delete('lng');
                return previous;
            },
            { replace: true },
        );

        if (isSupportedLanguage(urlLanguage)) {
            sendEvent('legacy_language_url_visited', {
                url_language: urlLanguage,
                path: window.location.pathname,
            });
        }
    }, [urlLanguage, setSearchParams, sendEvent]);

    return null;
}
